import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Skeleton,
    TextField,
    Typography,
} from '@mui/material';
import {
    formatTradeIdeaRange,
    parseStartingValueInputs,
    pickStartingValues,
    STARTING_VALUE_INPUTS,
    startingValueInputs,
    tradeIdeaRangeLimit,
} from '../../utils/posSizingStartingValues';

const startingValuesShape = PropTypes.shape({
    max_pos_size: PropTypes.number.isRequired,
    normal_vol: PropTypes.number.isRequired,
    target_vol_pct: PropTypes.number.isRequired,
    target_vol_dollar: PropTypes.number.isRequired,
    scaler: PropTypes.number.isRequired,
});
const formatAum = (value) => value.toLocaleString('en-US', { maximumFractionDigits: 6 });

/**
 * Asks a member for their starting values before their first Position Sizing
 * draft. Mounted only while the choice is needed; it has no other way out, so
 * the draft waits for the choice.
 */
export default function PosSizingStartingValuesDialog({
    picked, suggestion = null, aum = null, aumStored = false,
    aumLoading = false, aumError = '', onRetryAum, onChoose,
}) {
    const [inputs, setInputs] = useState(() => startingValueInputs(picked));
    const [aumInput, setAumInput] = useState(() => Number.isFinite(aum) ? formatAum(aum) : '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const parsed = parseStartingValueInputs(inputs);
    const rangeText = formatTradeIdeaRange(tradeIdeaRangeLimit(suggestion, parsed.values));
    const parsedAum = Number(aumInput.replace(/,/g, '').trim());
    const validAum = aumInput.trim() !== '' && Number.isFinite(parsedAum) && parsedAum > 0;
    const aumReady = !aumLoading && !aumError && Number.isFinite(aum) && aum > 0;
    useEffect(() => { setAumInput(Number.isFinite(aum) ? formatAum(aum) : ''); }, [aum]);
    const aumHelperText = aumInput !== '' && !validAum ? 'Enter a number above 0.'
        : aumReady ? aumStored
            ? `Your current AUM is $${formatAum(aum)}. “Pick for me” keeps this amount.`
            : `If you’re not sure, “Pick for me” uses $${formatAum(aum)}.`
            : undefined;

    const save = async (values) => {
        setSaving(true);
        setError('');
        try {
            // On success the prompt closes and this dialog unmounts.
            await onChoose(values);
        } catch (saveError) {
            console.error('Error saving Position Sizing starting values:', saveError);
            setError(saveError?.message || 'Your choice could not be saved. Try again.');
            setSaving(false);
        }
    };

    const handleUseValues = () => {
        if (parsed.values && validAum && aumReady) void save({ ...parsed.values, aum: parsedAum });
    };

    const handlePickForMe = () => {
        setInputs(startingValueInputs(picked));
        setAumInput(formatAum(aum));
        void save({ ...pickStartingValues(picked), aum });
    };

    return (
        <Dialog
            open
            disableEscapeKeyDown
            maxWidth="xs"
            fullWidth
            aria-labelledby="pos-sizing-starting-values-title"
        >
            <DialogTitle id="pos-sizing-starting-values-title">
                Set up your portfolio and position sizing
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Set your assets under management (AUM) and initial sizing values. You can change AUM from Risk Overview and sizing values from Sizing defaults.
                </Typography>
                <Box sx={{ display: 'grid', gap: 1.75 }}>
                    {aumLoading ? (
                        <Box role="status" aria-label="Loading assets under management">
                            <Skeleton animation="wave" variant="rounded" height={40} />
                            <Typography variant="caption" color="text.secondary">Loading your AUM…</Typography>
                        </Box>
                    ) : aumError ? (
                        <Alert severity="warning" action={<Button onClick={onRetryAum}>Retry</Button>}>{aumError}</Alert>
                    ) : (
                        <TextField
                            label="Assets under management (USD)"
                            size="small"
                            value={aumInput}
                            onChange={(event) => setAumInput(event.target.value)}
                            disabled={saving || !aumReady}
                            error={aumInput !== '' && !validAum}
                            helperText={aumHelperText}
                            inputProps={{ inputMode: 'decimal' }}
                            fullWidth
                        />
                    )}
                    {STARTING_VALUE_INPUTS.map(({ field, label }) => {
                        const fieldError = inputs[field] !== '' ? parsed.errors[field] : '';
                        const helperText = fieldError
                            || (field === 'target_vol_dollar' ? rangeText : '');
                        return (
                            <TextField
                                key={field}
                                label={label}
                                size="small"
                                value={inputs[field]}
                                onChange={(event) => setInputs((current) => ({
                                    ...current,
                                    [field]: event.target.value,
                                }))}
                                error={Boolean(fieldError)}
                                helperText={helperText || undefined}
                                disabled={saving}
                                inputProps={{ inputMode: 'decimal' }}
                                fullWidth
                            />
                        );
                    })}
                </Box>
                {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    onClick={handlePickForMe}
                    disabled={saving || !aumReady}
                    sx={{ textTransform: 'none' }}
                >
                    I&apos;m not sure — pick for me
                </Button>
                <Button
                    variant="contained"
                    onClick={handleUseValues}
                    disabled={saving || !parsed.values || !validAum || !aumReady}
                    sx={{ textTransform: 'none' }}
                >
                    Use these values
                </Button>
            </DialogActions>
        </Dialog>
    );
}

PosSizingStartingValuesDialog.propTypes = {
    picked: startingValuesShape.isRequired,
    suggestion: PropTypes.shape({
        targetVolDollar: PropTypes.number,
        tradeIdeaScale: PropTypes.number,
        basis: PropTypes.object,
    }),
    aum: PropTypes.number,
    aumStored: PropTypes.bool,
    aumLoading: PropTypes.bool,
    aumError: PropTypes.string,
    onRetryAum: PropTypes.func,
    onChoose: PropTypes.func.isRequired,
};

export { startingValuesShape };
